import { readFile } from "node:fs/promises";
import { isAbsolute, join, normalize } from "node:path";

import { ConvexStore } from "./convex-store.js";
import { FileStore } from "./file-store.js";
import type { FathomStore, StoreConfig } from "./types.js";

/**
 * SEC-04: Validate that a store path is relative and does not escape the project directory.
 */
function validateStorePath(storePath: string): void {
  if (isAbsolute(storePath)) {
    throw new Error(`Store path must be relative, got absolute path: "${storePath}"`);
  }
  const normalized = normalize(storePath);
  if (normalized.startsWith("..")) {
    throw new Error(`Store path must not escape project directory: "${storePath}"`);
  }
}

/**
 * Load store config from `.fathom/config.json` in the given directory.
 * Returns null if file doesn't exist or has no store config.
 */
export async function loadStoreConfig(
  dir: string,
): Promise<StoreConfig | null> {
  try {
    const raw = await readFile(join(dir, ".fathom", "config.json"), "utf-8");
    const config = JSON.parse(raw) as Record<string, unknown>;

    if (config.store && typeof config.store === "object") {
      const store = config.store as Record<string, unknown>;
      if (store.type === "file" && typeof store.path === "string") {
        validateStorePath(store.path);
        return { type: "file", path: store.path };
      }
      if (store.type === "convex" && typeof store.url === "string") {
        return { type: "convex", url: store.url };
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Create a FathomStore instance based on config.
 *
 * Resolution order:
 * 1. Explicit config parameter
 * 2. `.fathom/config.json` in cwd
 * 3. Default: file store at `.fathom/store/`
 */
export function createStore(config?: StoreConfig): FathomStore {
  if (!config) {
    // Default to file store in cwd
    return new FileStore(join(process.cwd(), ".fathom", "store"));
  }

  switch (config.type) {
    case "file":
      validateStorePath(config.path);
      return new FileStore(join(process.cwd(), config.path));
    case "convex":
      return new ConvexStore({ url: config.url });
    default:
      throw new Error(`Unknown store type: ${(config as StoreConfig).type}`);
  }
}

/**
 * Create a FathomStore, checking for config file first.
 * Async version that reads `.fathom/config.json` if no explicit config given.
 */
export async function createStoreFromConfig(
  dir?: string,
): Promise<FathomStore> {
  const baseDir = dir ?? process.cwd();
  const config = await loadStoreConfig(baseDir);

  if (config) {
    return createStore(config);
  }

  // Default: file store
  return new FileStore(join(baseDir, ".fathom", "store"));
}
